import Paper from "@mui/material/Paper";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router";
import Loader from "_shared/_views/loading/loader";
import VendorRolloutStatusBoard from "legal-entities/vendor-rollouts/_views/vendor-rollout-status-board";
import { firmOnboardingTabPath, FirmOnboardingTab } from "legal-entities/vendor-rollouts/firm-onboarding-routes";
import "permissions/__extensions/users/user-extensions";
import useSessionUser from "permissions/session/use-session-user";
import FirmOnboardingDefaults from "work/client-proposal-templates/_views/firm-onboarding-defaults";

// Bounded to the shell's content section, which clips with `overflow: hidden` and does not scroll
// itself. Without a height limit the paper grows past the viewport and its own `overflow: auto`
// never engages, so a long board is cut off rather than scrolled — and on the Defaults tab, the
// Save button below the fold cannot be reached.
const PageContainer = styled(Paper)(({ theme }) => ({
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  margin: theme.spacing(1),
  maxHeight: `calc(100% - ${theme.spacing(2)})`,
  overflow: "auto",
  padding: theme.spacing(2),
}));
const PageTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(0.5),
}));
const PageTabs = styled(Tabs)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));
const PageIntroduction = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(2),
  maxWidth: "48rem",
}));

/**
 * The Firm Onboarding page: how far each of the firms this organization invited has got, and on its
 * Defaults tab, what a firm's draft proposal is built from when it gets there (#6261).
 *
 * The client's own half of the status board. It exists so nobody has to wait to be told: a client
 * who has just asked all of their outside counsel to change how they bill reads silence as failure,
 * whether or not anything is actually wrong.
 *
 * Each tab has its own address, so a link can open the Defaults directly and the side navigation
 * stays on Firm Onboarding for both.
 */
export default function FirmOnboardingStatusPage(props: Readonly<{ tab?: FirmOnboardingTab }>) {
  const tab = props.tab ?? FirmOnboardingTab.Firms;
  const user = useSessionUser();
  const navigate = useNavigate();

  if (!user) return <Loader text="Loading firm onboarding status..." />;

  return (
    <PageContainer>
      <PageTitle variant="h5">Firm onboarding</PageTitle>
      <PageTabs
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        value={tab}
        onChange={(_event, value: FirmOnboardingTab) => navigate(firmOnboardingTabPath(value))}
      >
        <Tab label="Firms" value={FirmOnboardingTab.Firms} />
        {user.canViewClientProposalTemplates() && <Tab label="Defaults" value={FirmOnboardingTab.Defaults} />}
      </PageTabs>
      {tab === FirmOnboardingTab.Firms && (
        <>
          <PageIntroduction variant="body2">
            How far each firm you have invited has got. Every date here is a step the platform
            recorded, not a status anybody typed in.
          </PageIntroduction>
          <VendorRolloutStatusBoard
            clientLegalEntityId={user.legalEntityId}
            emptyTitle="No firms yet"
            emptyDescription="Firms appear here once invitations go out to their team leaders."
          />
        </>
      )}
      {tab === FirmOnboardingTab.Defaults && <FirmOnboardingDefaults />}
    </PageContainer>
  );
}
